// EmployeeOnboarding.js v1.1 - Added phone auto-formatting
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, User, FileText, FileCheck, CreditCard, Heart, Shield, AlertCircle, Upload, ChevronDown, Sun, Moon, DollarSign, Car } from 'lucide-react';
import { colors, LYT_INFO, URLS } from '../config/constants';
import { getTheme, getInputStyle } from '../utils/theme';
import SignaturePad from '../components/SignaturePad';
import SSNInput from '../components/SSNInput';
import { fillW4, fillW9, createFormPdf } from '../services/pdfService';

// Format phone number as (000) 000-0000
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
};

const EmployeeOnboarding = ({ setCurrentPage, onboardingContext, darkMode, setDarkMode }) => {
  const is1099 = !!onboardingContext;
  const theme = getTheme(darkMode);
  const { accentMarketing: accentPrimary, accentMarketingAlt: accentSecondary, error: accentError, logoLY, logoT, logoComm, bgAlt: bgColor, cardBg, text: textColor } = theme.colors;
  // eslint-disable-next-line no-unused-vars
  const accentGradient = theme.colors.gradientMarketing;

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [showW4Pdf, setShowW4Pdf] = useState(false);
  const [showW9Pdf, setShowW9Pdf] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [showStepDropdown, setShowStepDropdown] = useState(false);
  
  // Hidden skip feature - triple tap counter
  const [skipTapCount, setSkipTapCount] = useState(0);
  const [skipTapTimer, setSkipTapTimer] = useState(null);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Hidden skip: triple-tap on header title to skip to next step
  const handleHeaderTap = () => {
    if (skipTapTimer) clearTimeout(skipTapTimer);
    
    const newCount = skipTapCount + 1;
    setSkipTapCount(newCount);
    
    if (newCount >= 3) {
      // Triple tap detected - skip to next step (or cycle back)
      setCurrentStep((prev) => (prev + 1) % steps.length);
      setSkipTapCount(0);
    } else {
      // Reset counter after 500ms of no taps
      const timer = setTimeout(() => setSkipTapCount(0), 500);
      setSkipTapTimer(timer);
    }
  };

  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    dateOfBirth: '',
    ssn: '',
    // W-4 Info
    filingStatus: 'single',
    multipleJobs: false,
    qualifyingChildren: 0,
    otherDependents: 0,
    otherIncome: '',
    deductions: '',
    extraWithholding: '',
    exempt: false,
    // W-4 Page 3 Worksheet (Step 2b - Multiple Jobs)
    worksheet: {
      step2b_line1: '',
      step2b_line2a: '',
      step2b_line2b: '',
      step2b_line2c: '',
      step2b_line3: '',
      step2b_line4: '',
    },
    // W-4 Page 4 Worksheet (Step 4b - Deductions)
    deductionsWorksheet: {
      line1a: '',
      line1b: '',
      line1c: '',
      line1d: '',
      line3a: '',
      line3b: '',
      line3c: '',
      line5: '',
      line6a: '',
      line6b: '',
      line6c: '',
      line6d: '',
      line6e: '',
      line7: '',
      line8a: '',
      line8b: '',
      line9: '',
      line10: '',
      line11: '',
      line12: '',
      line13: '',
      line14: '',
      line15: '',
    },
    showWorksheets: false,
    showAdvanced: false,
    w4Signature: null,
    w4Date: new Date().toISOString().split('T')[0],
    // Direct Deposit
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    voidedCheck: null,
    voidedCheckName: '',
    directDepositAgreed: false,
    directDepositSignature: null,
    directDepositDate: new Date().toISOString().split('T')[0],
    // ID Verification
    idType: 'drivers-license',
    idFile: null,
    idFileName: '',
    // Emergency Contact
    emergencyName: '',
    emergencyRelation: '',
    emergencyPhone: '',
    emergencyEmail: '',
    // Background & Drug Test Consents
    backgroundCheckConsent: false,
    backgroundCheckSignature: null,
    drugTestConsent: false,
    drugTestSignature: null,
    consentsDate: new Date().toISOString().split('T')[0],
    // Safety
    safetyAcknowledged: false,
    safetySignature: null,
    safetyDate: new Date().toISOString().split('T')[0],
    // Vehicle Info (1099 mode, e.g. DFW airport badge)
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    vehiclePlate: '',
    vehiclePlateState: '',
    // W-9 (1099 mode only)
    w9Signature: null,
    w9Date: new Date().toISOString().split('T')[0],
    taxClassification: 'individual',
    // ICA + Rate Card (1099 mode only)
    icaAccepted: false,
    icaSignature: null,
    icaDate: new Date().toISOString().split('T')[0],
    rateCardAccepted: false,
    rateCardSignature: null,
    // Rate input (lytnew general workers)
    rateType: 'hourly',
    rateAmount: '',
  });

  // 1099 mode: ICA + Rate Card instead of W-4, drop background/drug consents
  const needsVehicle = is1099 && onboardingContext?.requireVehicle;
  const steps = is1099 ? [
    { id: 'personal', title: 'Personal Info', icon: User },
    ...(needsVehicle ? [{ id: 'vehicle', title: 'Vehicle Details', icon: Car }] : []),
    { id: 'w9', title: 'W-9 Tax Form', icon: FileCheck },
    { id: 'ica', title: 'Contractor Agreement', icon: FileText },
    { id: 'rate-card', title: 'Rate Card', icon: DollarSign },
    { id: 'direct-deposit', title: 'Direct Deposit', icon: CreditCard },
    { id: 'id-verification', title: 'ID Verification', icon: FileText },
    { id: 'emergency', title: 'Emergency Contact', icon: Heart },
    { id: 'safety', title: 'Safety & Agreement', icon: Shield },
  ] : [
    { id: 'personal', title: 'Personal Info', icon: User },
    { id: 'w4', title: 'W-4 Tax Form', icon: FileText },
    { id: 'direct-deposit', title: 'Direct Deposit', icon: CreditCard },
    { id: 'id-verification', title: 'ID Verification', icon: FileText },
    { id: 'emergency', title: 'Emergency Contact', icon: Heart },
    { id: 'consents', title: 'Consents', icon: Shield },
    { id: 'safety', title: 'Safety Training', icon: Shield },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Auto-format phone numbers
    if (name === 'phone' || name === 'emergencyPhone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleSSNChange = (value) => {
    setFormData({ ...formData, ssn: value });
  };

  const handleW4SignatureChange = (signature) => {
    setFormData({ ...formData, w4Signature: signature });
  };

  const handleSafetySignatureChange = (signature) => {
    setFormData({ ...formData, safetySignature: signature });
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // DFW badge requirement: vehicle details must be complete before submit
    if (needsVehicle && !(formData.vehicleYear && formData.vehicleMake && formData.vehicleModel && formData.vehicleColor && formData.vehiclePlate && formData.vehiclePlateState)) {
      setError('Vehicle details are required: year, make, model, color, plate, and plate state.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // ========== CAPTURE IP ADDRESS FOR ESIGN COMPLIANCE ==========
      let ipAddress = 'Unable to capture';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (ipErr) {
        console.log('IP capture failed:', ipErr);
      }
      const signatureTimestamp = new Date().toISOString();
      
      // ========== GENERATE FILLED PDFs ==========
      console.log('Generating filled PDFs...');
      
      // 0. Personal Information PDF (captures all Step 1 data)
      let personalInfoPdf = null;
      try {
        personalInfoPdf = await createFormPdf(
          'Employee Personal Information',
          [
            { title: 'PERSONAL DETAILS', fields: [
              { label: 'Full Name', value: fullName },
              { label: 'Email', value: formData.email },
              { label: 'Phone', value: formData.phone },
              { label: 'Date of Birth', value: formData.dateOfBirth },
              { label: 'SSN', value: formData.ssn || '' },
            ]},
            { title: 'ADDRESS', fields: [
              { label: 'Street', value: formData.address },
              { label: 'City', value: formData.city },
              { label: 'State', value: formData.state },
              { label: 'ZIP', value: formData.zip },
            ]},
            ...(formData.vehiclePlate ? [{ title: 'VEHICLE', fields: [
              { label: 'Year/Make/Model', value: `${formData.vehicleYear} ${formData.vehicleMake} ${formData.vehicleModel}` },
              { label: 'Color', value: formData.vehicleColor },
              { label: 'License Plate', value: `${formData.vehiclePlate} (${formData.vehiclePlateState})` },
            ]}] : []),
            { title: 'ELECTRONIC RECORD', fields: [
              { label: 'IP Address', value: ipAddress },
              { label: 'Timestamp', value: signatureTimestamp },
              { label: 'Submitted', value: new Date().toLocaleString() },
            ]}
          ],
          null,
          { timestamp: new Date().toLocaleString(), ip: ipAddress }
        );
        console.log('Personal Info PDF created');
      } catch (e) {
        console.error('Personal Info error:', e);
      }
      
      // 1. Fill actual IRS W-4 form (W-2 employees only) or W-9 + Rate Card (1099 workers)
      let w4Pdf = null;
      let w9Pdf = null;
      let rateCardPdf = null;
      const signatureInfo = {
        timestamp: new Date().toLocaleString(),
        ip: ipAddress
      };

      // W-9 PDF (1099 workers)
      if (is1099) {
        try {
          w9Pdf = await fillW9({
            companyName: fullName,
            dba: '',
            entityType: 'individual',
            taxClassification: formData.taxClassification || 'individual',
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            ein: '',
            ssn: formData.ssn,
          }, formData.w9Signature, signatureInfo);
          console.log('W-9 PDF filled successfully');
        } catch (e) {
          console.error('W-9 error:', e);
        }
      }

      // ICA PDF (1099 workers)
      let icaPdf = null;
      if (is1099) {
        try {
          icaPdf = await createFormPdf(
            'Independent Contractor Agreement',
            [
              { title: 'PARTIES', fields: [
                { label: 'Company', value: LYT_INFO.name },
                { label: 'Contractor', value: fullName },
                { label: 'Email', value: formData.email },
                { label: 'Phone', value: formData.phone },
                { label: 'Address', value: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}` },
              ]},
              { title: 'PROJECT', fields: [
                { label: 'Project', value: onboardingContext.project },
                { label: 'Client', value: onboardingContext.client },
                { label: 'Position', value: onboardingContext.role || 'As assigned' },
                { label: 'Payment Terms', value: onboardingContext.paymentTerms || 'Weekly' },
              ]},
              { title: 'KEY TERMS', checkboxes: [
                { label: 'Independent contractor (1099), not employee', checked: true },
                { label: 'Responsible for own federal, state, and local taxes', checked: true },
                { label: 'Not entitled to employee benefits', checked: true },
                { label: 'At-will engagement, terminable by either party', checked: true },
                { label: 'Confidentiality and non-solicitation obligations', checked: true },
                { label: 'Compliance with all safety and site rules', checked: true },
                { label: 'Consent to background check and drug testing', checked: true },
                { label: 'Indemnification of Company', checked: true },
                { label: 'Governed by Texas law, Harris County arbitration', checked: true },
              ]},
              { paragraphs: [
                'By signing below, Contractor acknowledges reading and agreeing to the full Independent Contractor Agreement including all 14 sections covering: contractor status, scope of work, compensation, taxes, insurance, indemnification, confidentiality, non-solicitation, safety, drug policy, background checks, termination, dispute resolution, and entire agreement.',
              ]},
            ],
            formData.icaSignature,
            signatureInfo
          );
          console.log('ICA PDF created');
        } catch (e) {
          console.error('ICA error:', e);
        }
      }

      if (is1099 && onboardingContext.allowRateInput) {
        // Generate Proposed Rate PDF for approval-required workers
        try {
          const rateLabel = formData.rateType === 'hourly' ? 'Hourly' : 'Day Rate';
          const rateUnit = formData.rateType === 'hourly' ? 'HR' : 'DAY';
          rateCardPdf = await createFormPdf(
            'Proposed Rate — Independent Contractor',
            [
              { title: 'WORKER', fields: [
                { label: 'Name', value: fullName },
                { label: 'Email', value: formData.email },
                { label: 'Phone', value: formData.phone },
              ]},
              { title: 'PROPOSED PAY RATE', fields: [
                { label: 'Rate Type', value: rateLabel },
                { label: 'Proposed Rate', value: `$${parseFloat(formData.rateAmount).toFixed(2)} / ${rateUnit}` },
                { label: 'Payment Terms', value: onboardingContext.paymentTerms || 'Weekly' },
              ]},
              { title: 'STATUS', fields: [
                { label: 'Approval', value: 'PENDING — Awaiting office approval' },
              ]},
              { title: '1099 ACKNOWLEDGMENT', paragraphs: [
                'I understand I am being engaged as an independent contractor (1099) and am responsible for my own federal, state, and local taxes.',
                'I understand this proposed rate is subject to approval by LYT Communications management.',
              ]},
            ],
            formData.rateCardSignature,
            signatureInfo
          );
          console.log('Proposed Rate PDF created');
        } catch (e) {
          console.error('Proposed Rate Card error:', e);
        }
      } else if (is1099 && onboardingContext.rates && onboardingContext.rates.length > 0) {
        // Generate Rate Card Acceptance PDF for 1099 workers with fixed rates
        try {
          rateCardPdf = await createFormPdf(
            'Rate Card Acceptance — Independent Contractor',
            [
              { title: 'WORKER', fields: [
                { label: 'Name', value: fullName },
                { label: 'Email', value: formData.email },
                { label: 'Phone', value: formData.phone },
              ]},
              { title: 'PROJECT', fields: [
                { label: 'Project', value: onboardingContext.project },
                { label: 'Client', value: onboardingContext.client },
                { label: 'Position', value: onboardingContext.role || 'As assigned' },
                { label: 'Payment Terms', value: onboardingContext.paymentTerms || 'Weekly' },
              ]},
              { title: 'PAY RATE', fields: onboardingContext.rates.map(r => ({
                label: r.description,
                value: `${r.price} / ${r.uom}`,
              }))},
              { title: '1099 ACKNOWLEDGMENT', paragraphs: [
                'I understand I am being engaged as an independent contractor (1099) and am responsible for my own federal, state, and local taxes.',
                'I am not an employee of LYT Communications, LLC and am not entitled to employee benefits.',
              ]},
            ],
            formData.rateCardSignature,
            signatureInfo
          );
          console.log('Rate Card PDF created');
        } catch (e) {
          console.error('Rate Card error:', e);
        }
      } else {
        // W-4 for W-2 employees
        try {
          w4Pdf = await fillW4({
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            ssn: formData.ssn,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            filingStatus: formData.filingStatus,
            multipleJobs: formData.multipleJobs,
            qualifyingChildren: formData.qualifyingChildren,
            otherDependents: formData.otherDependents,
            otherIncome: formData.otherIncome,
            deductions: formData.deductions,
            extraWithholding: formData.extraWithholding,
            worksheet: formData.worksheet,
            deductionsWorksheet: formData.deductionsWorksheet,
          }, formData.w4Signature, signatureInfo);
          console.log('W-4 PDF filled successfully');
        } catch (e) {
          console.error('W-4 error:', e);
        }
      }
      
      // 2. Direct Deposit Authorization (with embedded voided check)
      let directDepositPdf = null;
      let voidedCheckImage = null;
      
      // Read voided check image if available
      if (formData.voidedCheck) {
        try {
          const reader = new FileReader();
          voidedCheckImage = await new Promise((resolve, reject) => {
            reader.onload = () => resolve({
              data: reader.result,
              mimeType: formData.voidedCheck.type
            });
            reader.onerror = reject;
            reader.readAsDataURL(formData.voidedCheck);
          });
        } catch (e) {
          console.error('Error reading voided check:', e);
        }
      }
      
      try {
        directDepositPdf = await createFormPdf(
          'Direct Deposit Authorization',
          [
            { title: 'EMPLOYEE', fields: [
              { label: 'Name', value: fullName },
              { label: 'Email', value: formData.email },
              { label: 'Phone', value: formData.phone },
            ]},
            { title: 'BANK INFORMATION', fields: [
              { label: 'Bank Name', value: formData.bankName },
              { label: 'Routing Number', value: formData.routingNumber },
              { label: 'Account Number', value: formData.accountNumber || '' },
              { label: 'Account Type', value: formData.accountType },
            ]},
            { title: 'AUTHORIZATION', paragraphs: [
              'I authorize LYT Communications, LLC to initiate credit entries to my account.',
              'This authorization remains in effect until I provide written notice to discontinue.',
            ]},
            { title: 'ELECTRONIC SIGNATURE VERIFICATION', fields: [
              { label: 'IP Address', value: ipAddress },
              { label: 'Timestamp', value: signatureTimestamp },
            ]}
          ],
          formData.directDepositSignature,
          signatureInfo,
          voidedCheckImage
        );
        console.log('Direct Deposit PDF created');
      } catch (e) {
        console.error('Direct Deposit error:', e);
      }
      
      // 3. Emergency Contact
      let emergencyContactPdf = null;
      try {
        emergencyContactPdf = await createFormPdf(
          'Emergency Contact Information',
          [
            { title: 'EMPLOYEE', fields: [
              { label: 'Name', value: fullName },
              { label: 'Phone', value: formData.phone },
            ]},
            { title: 'EMERGENCY CONTACT', fields: [
              { label: 'Name', value: formData.emergencyName },
              { label: 'Relationship', value: formData.emergencyRelation },
              { label: 'Phone', value: formData.emergencyPhone },
              { label: 'Email', value: formData.emergencyEmail },
            ]}
          ],
          null,
          signatureInfo
        );
        console.log('Emergency Contact PDF created');
      } catch (e) {
        console.error('Emergency Contact error:', e);
      }
      
      // 4. Background Check Consent (W-2 only — 1099 workers consent via ICA Section 11)
      let backgroundCheckPdf = null;
      if (!is1099) {
        try {
          backgroundCheckPdf = await createFormPdf(
            'Background Check Authorization',
            [
              { title: 'APPLICANT', fields: [
                { label: 'Name', value: fullName },
                { label: 'DOB', value: formData.dateOfBirth },
                { label: 'SSN', value: formData.ssn || '' },
              ]},
              { title: 'AUTHORIZATION', paragraphs: [
                'I authorize LYT Communications, LLC to conduct a background investigation including criminal records, employment verification, education verification, and reference checks.',
              ], checkboxes: [
                { label: 'I authorize this background check', checked: formData.backgroundCheckConsent }
              ]},
              { title: 'ELECTRONIC SIGNATURE VERIFICATION', fields: [
                { label: 'IP Address', value: ipAddress },
                { label: 'Timestamp', value: signatureTimestamp },
              ]}
            ],
            formData.backgroundCheckSignature,
            signatureInfo
          );
          console.log('Background Check PDF created');
        } catch (e) {
          console.error('Background Check error:', e);
        }
      }

      // 5. Drug Test Consent (W-2 only — 1099 workers consent via ICA Section 10)
      let drugTestPdf = null;
      if (!is1099) {
        try {
          drugTestPdf = await createFormPdf(
            'Drug and Alcohol Testing Consent',
            [
              { title: 'EMPLOYEE', fields: [{ label: 'Name', value: fullName }]},
              { title: 'CONSENT', paragraphs: [
                'I consent to drug and alcohol testing as required by company policy.',
              ], checkboxes: [
                { label: 'Pre-employment testing required', checked: true },
                { label: 'Random testing may be conducted', checked: true },
                { label: 'Post-accident testing may be required', checked: true },
                { label: 'I consent to testing', checked: formData.drugTestConsent }
              ]},
              { title: 'ELECTRONIC SIGNATURE VERIFICATION', fields: [
                { label: 'IP Address', value: ipAddress },
                { label: 'Timestamp', value: signatureTimestamp },
              ]}
            ],
            formData.drugTestSignature,
            signatureInfo
          );
          console.log('Drug Test PDF created');
        } catch (e) {
          console.error('Drug Test error:', e);
        }
      }
      
      // 6. Safety Acknowledgment
      let safetyPdf = null;
      try {
        safetyPdf = await createFormPdf(
          'HSE Safety Acknowledgment',
          [
            { title: 'EMPLOYEE', fields: [{ label: 'Name', value: fullName }]},
            { title: 'ACKNOWLEDGMENT', paragraphs: [
              'I have received, read, and understand the LYT Communications HSE Manual.',
            ], checkboxes: [
              { label: 'Follow all safety procedures', checked: true },
              { label: 'Use required PPE', checked: true },
              { label: 'Report injuries immediately', checked: true },
              { label: 'Participate in safety training', checked: true },
              { label: 'I acknowledge the HSE Manual', checked: formData.safetyAcknowledged }
            ]},
            { title: 'ELECTRONIC SIGNATURE VERIFICATION', fields: [
              { label: 'IP Address', value: ipAddress },
              { label: 'Timestamp', value: signatureTimestamp },
            ]}
          ],
          formData.safetySignature,
          signatureInfo
        );
        console.log('Safety PDF created');
      } catch (e) {
        console.error('Safety error:', e);
      }

      // Prepare file uploads — name files after candidate
      const candidateName = `${formData.firstName}_${formData.lastName}`.replace(/[^a-zA-Z0-9_-]/g, '');
      let voidedCheckData = null;
      if (formData.voidedCheck) {
        const ext = formData.voidedCheckName.split('.').pop() || 'pdf';
        const reader = new FileReader();
        voidedCheckData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve({
            name: `${candidateName}_Voided_Check.${ext}`,
            data: reader.result.split(',')[1],
            mimeType: formData.voidedCheck.type
          });
          reader.onerror = reject;
          reader.readAsDataURL(formData.voidedCheck);
        });
      }

      let idFileData = null;
      if (formData.idFile) {
        const ext = formData.idFileName.split('.').pop() || 'pdf';
        const reader = new FileReader();
        idFileData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve({
            name: `${candidateName}_ID_Verification.${ext}`,
            data: reader.result.split(',')[1],
            mimeType: formData.idFile.type
          });
          reader.onerror = reject;
          reader.readAsDataURL(formData.idFile);
        });
      }

      // Build payload with filled PDFs
      // IMPORTANT: Use 'action' field (not 'type') with exact action name expected by Apps Script
      const payload = {
        action: is1099 ? 'submitWorkerOnboarding' : 'submitEmployeeOnboarding',
        workerType: is1099 ? '1099' : 'W-2',
        projectContext: is1099 ? {
          client: onboardingContext.client,
          project: onboardingContext.project,
          role: onboardingContext.role || null,
          paymentTerms: onboardingContext.paymentTerms,
          proposedRate: onboardingContext.allowRateInput ? {
            type: formData.rateType,
            amount: formData.rateAmount,
          } : null,
          requireApproval: onboardingContext.requireApproval || false,
        } : null,
        formData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          ssn: formData.ssn,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          bankName: formData.bankName,
          routingNumber: formData.routingNumber,
          accountNumber: formData.accountNumber,
          accountType: formData.accountType,
          emergencyName: formData.emergencyName,
          emergencyPhone: formData.emergencyPhone,
          emergencyRelation: formData.emergencyRelation,
          // Vehicle info (DFW airport badge)
          vehicleMake: formData.vehicleMake || '',
          vehicleModel: formData.vehicleModel || '',
          vehicleYear: formData.vehicleYear || '',
          vehicleColor: formData.vehicleColor || '',
          vehiclePlate: formData.vehiclePlate || '',
          vehiclePlateState: formData.vehiclePlateState || '',
        },
        // ESIGN compliance
        signatureVerification: {
          ipAddress: ipAddress,
          timestamp: signatureTimestamp,
          userAgent: navigator.userAgent,
        },
        // PRE-FILLED PDFs (base64)
        filledPdfs: {
          personalInfo: personalInfoPdf,
          w9: w9Pdf,
          ica: icaPdf,
          w4: w4Pdf,
          rateCard: rateCardPdf,
          directDeposit: directDepositPdf,
          emergencyContact: emergencyContactPdf,
          backgroundCheck: backgroundCheckPdf,
          drugTest: drugTestPdf,
          safety: safetyPdf,
        },
        // Suggested filenames for Google Drive (keyed to filledPdfs)
        fileNames: {
          personalInfo: `${candidateName}_Personal_Info.pdf`,
          w9: `${candidateName}_W9.pdf`,
          w4: `${candidateName}_W4.pdf`,
          ica: `${candidateName}_ICA.pdf`,
          rateCard: `${candidateName}_Rate_Card.pdf`,
          directDeposit: `${candidateName}_Direct_Deposit.pdf`,
          emergencyContact: `${candidateName}_Emergency_Contact.pdf`,
          backgroundCheck: `${candidateName}_Background_Check.pdf`,
          drugTest: `${candidateName}_Drug_Test.pdf`,
          safety: `${candidateName}_Safety_Acknowledgment.pdf`,
        },
        voidedCheck: voidedCheckData,
        idFile: idFileData,
        submittedAt: new Date().toISOString(),
      };

      console.log('Submitting with filled PDFs...');

      // Submit to Google Apps Script
      // Note: GAS returns a 302 redirect which can cause issues reading the response
      // The data IS processed even if we can't read the response
      let result = { success: false, error: 'Unknown error' };
      
      try {
        const response = await fetch(URLS.appsScript, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
        });

        const text = await response.text();

        // Try to parse as JSON
        try {
          result = JSON.parse(text);
        } catch (parseErr) {
          // GAS returns HTML with redirect - follow it to get actual JSON response
          if (text.includes('HREF="')) {
            const match = text.match(/HREF="([^"]+)"/i);
            if (match) {
              const redirectUrl = match[1].replace(/&amp;/g, '&');
              const finalResponse = await fetch(redirectUrl);
              const finalText = await finalResponse.text();
              try {
                result = JSON.parse(finalText);
              } catch (e) {
                result = { success: true, message: 'Submitted successfully' };
              }
            } else {
              result = { success: false, error: 'Server configuration error' };
            }
          } else if (text.includes('Page Not Found') || text.includes('unable to open')) {
            result = { success: false, error: 'Server error - please try again' };
          } else {
            result = { success: false, error: 'Unexpected response from server' };
          }
        }
      } catch (fetchErr) {
        console.error('Fetch error:', fetchErr);
        result = { success: false, error: 'Network error: ' + fetchErr.message };
      }
      
      console.log('Result:', result);

      if (result.success) {
        setSubmitted(true);
        // Send rate approval notification if this worker requires approval
        if (onboardingContext?.requireApproval && onboardingContext?.allowRateInput) {
          import('../services/notificationService').then(({ notifyWorkerRateApproval }) => {
            notifyWorkerRateApproval(
              fullName,
              formData.email,
              formData.phone,
              formData.rateType,
              formData.rateAmount,
              onboardingContext.project
            ).catch(err => console.warn('Rate approval notification failed:', err));
          });
        }
      } else {
        setError(result.error || 'Submission failed.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to submit: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Force light background for all form inputs to ensure text visibility
  // Using white background with dark text for maximum contrast in all modes
  const inputStyle = getInputStyle(darkMode);

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: textColor,
  };

  // Step content renderers
  const renderPersonalInfo = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>Personal Information</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>First Name *</label>
          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Middle Name</label>
          <input type="text" name="middleName" value={formData.middleName} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Last Name *</label>
          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Email *</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Phone *</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Date of Birth *</label>
          <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required style={inputStyle} />
        </div>
      </div>

      <SSNInput value={formData.ssn} onChange={handleSSNChange} darkMode={darkMode} />

      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Street Address *</label>
        <input type="text" name="address" value={formData.address} onChange={handleChange} required style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '16px' }}>
        <div>
          <label style={labelStyle}>City *</label>
          <input type="text" name="city" value={formData.city} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>State *</label>
          <input type="text" name="state" value={formData.state} onChange={handleChange} required maxLength={2} style={inputStyle} placeholder="TX" />
        </div>
        <div>
          <label style={labelStyle}>ZIP *</label>
          <input type="text" name="zip" value={formData.zip} onChange={handleChange} required style={inputStyle} />
        </div>
      </div>
    </div>
  );

  const renderVehicleInfo = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px' }}>Vehicle Information</h3>
      <p style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
        Required for airport security badge application. Enter the vehicle you'll be driving to the job site.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Vehicle Year *</label>
          <input type="text" name="vehicleYear" value={formData.vehicleYear} onChange={handleChange} required style={inputStyle} placeholder="2022" maxLength={4} />
        </div>
        <div>
          <label style={labelStyle}>Make *</label>
          <input type="text" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} required style={inputStyle} placeholder="Ford" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Model *</label>
          <input type="text" name="vehicleModel" value={formData.vehicleModel} onChange={handleChange} required style={inputStyle} placeholder="F-150" />
        </div>
        <div>
          <label style={labelStyle}>Color *</label>
          <input type="text" name="vehicleColor" value={formData.vehicleColor} onChange={handleChange} required style={inputStyle} placeholder="White" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '16px' }}>
        <div>
          <label style={labelStyle}>License Plate # *</label>
          <input type="text" name="vehiclePlate" value={formData.vehiclePlate} onChange={handleChange} required style={{ ...inputStyle, textTransform: 'uppercase' }} placeholder="ABC-1234" />
        </div>
        <div>
          <label style={labelStyle}>Plate State *</label>
          <input type="text" name="vehiclePlateState" value={formData.vehiclePlateState} onChange={handleChange} required maxLength={2} style={{ ...inputStyle, textTransform: 'uppercase' }} placeholder="TX" />
        </div>
      </div>
    </div>
  );

  const renderW4Form = () => {
    // Auto-calculate totals
    const childCredit = (parseInt(formData.qualifyingChildren) || 0) * 2000;
    const otherDependentCredit = (parseInt(formData.otherDependents) || 0) * 500;
    const totalDependentCredit = childCredit + otherDependentCredit;

    const helpTextStyle = {
      fontSize: '0.85rem',
      color: darkMode ? '#9ca3af' : '#6b7280',
      marginTop: '8px',
      lineHeight: '1.5',
    };

    const tipBoxStyle = {
      backgroundColor: darkMode ? '#1e3a5f' : '#eff6ff',
      border: `1px solid ${darkMode ? '#2563eb' : '#bfdbfe'}`,
      borderRadius: '8px',
      padding: '12px 16px',
      marginTop: '12px',
      fontSize: '0.85rem',
      color: darkMode ? '#93c5fd' : '#1e40af',
    };

    const sectionStyle = {
      marginBottom: '24px',
      padding: '20px',
      backgroundColor: darkMode ? '#111827' : '#f8fafc',
      borderRadius: '8px',
    };

    return (
      <div>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px' }}>Tax Withholding Setup</h3>
          <p style={helpTextStyle}>
            This form tells us how much federal tax to take out of each paycheck. Don't worry - we'll walk you through it step by step. Most people only need to answer 2-3 simple questions.
          </p>
        </div>

        {/* View PDF Button */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowW4Pdf(!showW4Pdf)}
            style={{ 
              padding: '8px 16px',
              backgroundColor: showW4Pdf ? accentPrimary : 'transparent',
              border: `1px solid ${accentPrimary}`,
              borderRadius: '6px',
              color: showW4Pdf ? '#fff' : accentPrimary,
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            {showW4Pdf ? 'Hide Official Form' : 'View Official IRS Form'}
          </button>
        </div>

        {showW4Pdf && (
          <div style={{ marginBottom: '24px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${darkMode ? '#374151' : '#ddd'}` }}>
            <iframe
              src={`${URLS.w4Pdf}#toolbar=0&navpanes=0&scrollbar=1`}
              style={{ width: '100%', height: isMobile ? '350px' : '600px', border: 'none', backgroundColor: '#fff' }}
              title="W-4 Form"
            />
          </div>
        )}

        {/* QUESTION 1: Filing Status */}
        <div style={sectionStyle}>
          <label style={{ ...labelStyle, marginBottom: '4px', fontSize: '1.1rem' }}>
            What's your tax filing status? *
          </label>
          <p style={helpTextStyle}>This is how you file (or plan to file) your federal tax return.</p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {/* Single */}
            <label style={{ 
              display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '12px', 
              backgroundColor: formData.filingStatus === 'single' ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', 
              borderRadius: '8px', border: `2px solid ${formData.filingStatus === 'single' ? accentPrimary : (darkMode ? '#374151' : '#e5e7eb')}` 
            }}>
              <input
                type="radio"
                name="filingStatus"
                value="single"
                checked={formData.filingStatus === 'single'}
                onChange={handleChange}
                style={{ width: '20px', height: '20px', marginTop: '2px' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Single</span>
                <p style={{ ...helpTextStyle, marginTop: '4px' }}>
                  Choose this if you're not married. Also choose this if you're legally married but filing a separate return from your spouse.
                </p>
              </div>
            </label>

            {/* Married */}
            <label style={{ 
              display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '12px', 
              backgroundColor: formData.filingStatus === 'married' ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', 
              borderRadius: '8px', border: `2px solid ${formData.filingStatus === 'married' ? accentPrimary : (darkMode ? '#374151' : '#e5e7eb')}` 
            }}>
              <input
                type="radio"
                name="filingStatus"
                value="married"
                checked={formData.filingStatus === 'married'}
                onChange={handleChange}
                style={{ width: '20px', height: '20px', marginTop: '2px' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Married Filing Jointly</span>
                <p style={{ ...helpTextStyle, marginTop: '4px' }}>
                  Choose this if you're legally married and you and your spouse file one tax return together. This usually gives the best tax benefits.
                </p>
              </div>
            </label>

            {/* Head of Household */}
            <label style={{ 
              display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '12px', 
              backgroundColor: formData.filingStatus === 'head' ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', 
              borderRadius: '8px', border: `2px solid ${formData.filingStatus === 'head' ? accentPrimary : (darkMode ? '#374151' : '#e5e7eb')}` 
            }}>
              <input
                type="radio"
                name="filingStatus"
                value="head"
                checked={formData.filingStatus === 'head'}
                onChange={handleChange}
                style={{ width: '20px', height: '20px', marginTop: '2px' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Head of Household</span>
                <p style={{ ...helpTextStyle, marginTop: '4px' }}>
                  Choose this if you're unmarried AND you pay more than half the cost of keeping up a home for yourself and a qualifying person (like a child or parent who lives with you).
                </p>
              </div>
            </label>
          </div>

          {/* What counts as married explanation */}
          <div style={{ ...tipBoxStyle, marginTop: '16px' }}>
            <strong>💍 What counts as "married"?</strong>
            <p style={{ marginTop: '6px' }}>
              You're considered married if you were legally married on the last day of the tax year. This includes:
            </p>
            <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
              <li>Traditional marriage recognized by your state</li>
              <li>Common-law marriage (if your state recognizes it)</li>
              <li>Same-sex marriage</li>
            </ul>
            <p style={{ marginTop: '8px' }}>
              You're considered unmarried if you're legally separated under a divorce or separate maintenance decree, or if your spouse didn't live in your home for the last 6 months of the year.
            </p>
          </div>
        </div>

        {/* QUESTION 2: Spouse Works (only if married) */}
        {formData.filingStatus === 'married' && (
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, marginBottom: '4px', fontSize: '1.1rem' }}>
              Does your spouse also work?
            </label>
            <p style={helpTextStyle}>
              If both you and your spouse have jobs, we need to adjust your withholding so you don't end up owing taxes at the end of the year.
            </p>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginTop: '16px' }}>
              <input
                type="checkbox"
                name="multipleJobs"
                checked={formData.multipleJobs}
                onChange={handleChange}
                style={{ width: '20px', height: '20px' }}
              />
              <span>Yes, my spouse works (or I have more than one job myself)</span>
            </label>
            
            {formData.multipleJobs && (
              <div style={tipBoxStyle}>
                <strong>💡 Tip:</strong> When both spouses work, the IRS recommends checking this box on BOTH of your W-4 forms (yours and your spouse's) to avoid owing taxes when you file your return.
              </div>
            )}
          </div>
        )}

        {/* QUESTION 3: Kids / Dependents */}
        <div style={sectionStyle}>
          <label style={{ ...labelStyle, marginBottom: '4px', fontSize: '1.1rem' }}>
            Do you have any dependents?
          </label>
          <p style={helpTextStyle}>
            Claiming dependents reduces how much tax is taken from your paycheck. If you don't have any dependents, just leave these at 0.
          </p>

          {/* Detailed explanation of dependents */}
          <div style={{ ...tipBoxStyle, marginTop: '12px', marginBottom: '20px' }}>
            <strong>👨‍👩‍👧‍👦 Who counts as a dependent?</strong>
            
            <div style={{ marginTop: '12px' }}>
              <strong>Qualifying Child (each = $2,000 credit):</strong>
              <p style={{ marginTop: '4px' }}>Your child, stepchild, foster child, sibling, or their descendant who meets ALL of these:</p>
              <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                <li>Under age 17 at the end of the year</li>
                <li>Lived with you for more than half the year</li>
                <li>Didn't pay for more than half of their own living expenses</li>
                <li>Is a U.S. citizen, national, or resident</li>
                <li>You're the only one claiming them</li>
              </ul>
            </div>

            <div style={{ marginTop: '16px' }}>
              <strong>Other Dependent (each = $500 credit):</strong>
              <p style={{ marginTop: '4px' }}>Someone who doesn't qualify as a "child" but meets these requirements:</p>
              <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                <li>Is a relative (parent, grandparent, aunt, uncle, in-law, etc.) OR lived with you all year</li>
                <li>Earned less than $4,700 in 2024</li>
                <li>You provided more than half of their financial support</li>
                <li>Is a U.S. citizen, national, or resident</li>
              </ul>
            </div>

            <div style={{ marginTop: '16px', padding: '10px', backgroundColor: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '6px' }}>
              <strong>⚠️ Important:</strong> Your spouse is NEVER a dependent - they're handled by your filing status above, not here.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ ...labelStyle, fontSize: '0.95rem' }}>Children under 17</label>
              <p style={{ ...helpTextStyle, marginBottom: '8px', marginTop: '4px' }}>$2,000 tax credit each</p>
              <input
                type="number"
                name="qualifyingChildren"
                value={formData.qualifyingChildren}
                onChange={handleChange}
                min={0}
                max={20}
                style={inputStyle}
                placeholder="0"
              />
              {formData.qualifyingChildren > 0 && (
                <p style={{ color: colors.green, marginTop: '8px', fontWeight: '600' }}>
                  = ${childCredit.toLocaleString()} credit
                </p>
              )}
            </div>
            
            <div>
              <label style={{ ...labelStyle, fontSize: '0.95rem' }}>Other dependents</label>
              <p style={{ ...helpTextStyle, marginBottom: '8px', marginTop: '4px' }}>$500 tax credit each</p>
              <input
                type="number"
                name="otherDependents"
                value={formData.otherDependents}
                onChange={handleChange}
                min={0}
                max={20}
                style={inputStyle}
                placeholder="0"
              />
              {formData.otherDependents > 0 && (
                <p style={{ color: colors.green, marginTop: '8px', fontWeight: '600' }}>
                  = ${otherDependentCredit.toLocaleString()} credit
                </p>
              )}
            </div>
          </div>

          {totalDependentCredit > 0 && (
            <div style={{ marginTop: '20px', padding: '16px', backgroundColor: darkMode ? '#064e3b' : '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: '600', color: darkMode ? '#6ee7b7' : '#065f46' }}>
                🎉 Total Dependent Tax Credit: ${totalDependentCredit.toLocaleString()}
              </span>
              <p style={{ fontSize: '0.85rem', color: darkMode ? '#a7f3d0' : '#047857', marginTop: '4px' }}>
                This amount will reduce your tax bill!
              </p>
            </div>
          )}
        </div>

        {/* QUESTION 4: Extra Withholding */}
        <div style={sectionStyle}>
          <label style={{ ...labelStyle, marginBottom: '4px', fontSize: '1.1rem' }}>
            Want extra tax withheld? (Optional)
          </label>
          <p style={helpTextStyle}>
            Most people skip this section. But if you've owed money when filing taxes before, you can have us take out a little extra from each paycheck to avoid that surprise.
          </p>
          
          <div style={{ marginTop: '16px' }}>
            <label style={{ ...labelStyle, fontSize: '0.95rem' }}>Extra amount per paycheck</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <span style={{ fontSize: '1.1rem' }}>$</span>
              <input
                type="number"
                name="extraWithholding"
                value={formData.extraWithholding}
                onChange={handleChange}
                min={0}
                style={{ ...inputStyle, maxWidth: '150px' }}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Advanced Options (hidden by default) */}
        <div style={sectionStyle}>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: textColor,
            }}
          >
            <span style={{ fontWeight: '500' }}>⚙️ Advanced Options (most people skip this)</span>
            <span style={{ fontSize: '1.2rem' }}>{formData.showAdvanced ? '−' : '+'}</span>
          </button>
          
          {formData.showAdvanced && (
            <div style={{ marginTop: '16px' }}>
              <p style={helpTextStyle}>These options are for special situations. If you're not sure what they mean, leave them blank.</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.9rem' }}>Other income (not from jobs)</label>
                  <p style={{ ...helpTextStyle, fontSize: '0.8rem', marginBottom: '6px' }}>Interest, dividends, retirement distributions, etc.</p>
                  <input
                    type="number"
                    name="otherIncome"
                    value={formData.otherIncome}
                    onChange={handleChange}
                    placeholder="$0"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: '0.9rem' }}>Itemized deductions</label>
                  <p style={{ ...helpTextStyle, fontSize: '0.8rem', marginBottom: '6px' }}>Only if greater than standard deduction (~$14,600)</p>
                  <input
                    type="number"
                    name="deductions"
                    value={formData.deductions}
                    onChange={handleChange}
                    placeholder="$0"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Exempt checkbox */}
              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: darkMode ? '#7f1d1d' : '#fef2f2', borderRadius: '8px', border: `1px solid ${darkMode ? '#dc2626' : '#fecaca'}` }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="exempt"
                    checked={formData.exempt}
                    onChange={handleChange}
                    style={{ width: '18px', height: '18px', marginTop: '2px' }}
                  />
                  <div>
                    <span style={{ fontWeight: '500', color: darkMode ? '#fca5a5' : '#b91c1c' }}>I claim EXEMPT from withholding</span>
                    <p style={{ ...helpTextStyle, color: darkMode ? '#fca5a5' : '#991b1b', marginTop: '4px' }}>
                      ⚠️ Only check this if BOTH are true: (1) Last year you got a full refund of ALL federal income tax withheld, AND (2) This year you expect the same. This is rare - most people should NOT check this.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Signature Section */}
        <div style={sectionStyle}>
          <label style={{ ...labelStyle, marginBottom: '4px', fontSize: '1.1rem' }}>
            Sign to Complete *
          </label>
          <p style={helpTextStyle}>
            By signing below, you certify that the information you've provided is true and correct to the best of your knowledge.
          </p>
          
          <div style={{ marginTop: '16px' }}>
            <SignaturePad onSignatureChange={handleW4SignatureChange} label="Your Signature" darkMode={darkMode} />
          </div>

          {/* Auto-filled info display */}
          <div style={{ marginTop: '20px', padding: '16px', backgroundColor: darkMode ? '#1f2937' : '#f3f4f6', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '12px' }}>✅ These fields are auto-filled from your information:</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
              <div><strong>Name:</strong> {formData.firstName} {formData.middleName} {formData.lastName}</div>
              <div><strong>SSN:</strong> ***-**-{(formData.ssn || '').slice(-4)}</div>
              <div><strong>Address:</strong> {formData.address}</div>
              <div><strong>City/State/ZIP:</strong> {formData.city}, {formData.state} {formData.zip}</div>
              <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
              <div><strong>Employer:</strong> LYT Communications, LLC</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDirectDeposit = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>Direct Deposit Authorization</h3>
      
      <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', marginBottom: '20px', lineHeight: '1.6' }}>
        Provide your banking details for payment via ACH direct deposit. A voided check is required for verification.
      </p>
      
      {/* Bank Information */}
      <div style={{ padding: '20px', backgroundColor: darkMode ? '#1f2937' : '#f8fafc', borderRadius: '8px', marginBottom: '24px' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: accentPrimary }}>Bank Account Information</h4>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Bank Name *</label>
          <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} required style={inputStyle} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Routing Number (ABA) *</label>
            <input
              type="text"
              name="routingNumber"
              value={formData.routingNumber}
              onChange={handleChange}
              required
              maxLength={9}
              style={inputStyle}
              placeholder="9 digits"
            />
          </div>
          <div>
            <label style={labelStyle}>Account Number *</label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Account Type *</label>
          <div style={{ display: 'flex', gap: '20px' }}>
            {['checking', 'savings'].map((type) => (
              <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: textColor }}>
                <input
                  type="radio"
                  name="accountType"
                  value={type}
                  checked={formData.accountType === type}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ textTransform: 'capitalize' }}>{type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Voided Check Upload */}
      <div style={{ padding: '20px', backgroundColor: darkMode ? '#1f2937' : '#f8fafc', borderRadius: '8px', marginBottom: '24px' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: accentPrimary }}>Voided Check *</h4>
        <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '0.9rem', marginBottom: '16px' }}>
          Please upload an image of a voided check for bank account verification.
        </p>
        <div style={{ 
          border: `2px dashed ${formData.voidedCheckName ? colors.green : (darkMode ? '#374151' : '#ddd')}`, 
          borderRadius: '8px', 
          padding: '24px', 
          textAlign: 'center',
          backgroundColor: darkMode ? 'rgba(255,255,255,0.02)' : '#fff'
        }}>
          {formData.voidedCheckName ? (
            <div>
              <p style={{ color: colors.green, fontWeight: '500', marginBottom: '8px' }}>✓ {formData.voidedCheckName}</p>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, voidedCheck: null, voidedCheckName: '' })}
                style={{ padding: '8px 16px', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                id="voidedCheck"
                accept="image/*,.pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData({ ...formData, voidedCheck: file, voidedCheckName: file.name });
                  }
                }}
                style={{ display: 'none' }}
              />
              <label htmlFor="voidedCheck" style={{ cursor: 'pointer' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Upload size={32} color={accentPrimary} />
                </div>
                <p style={{ color: accentPrimary, fontWeight: '500' }}>Click to upload voided check</p>
                <p style={{ fontSize: '0.8rem', color: colors.gray }}>PDF, JPG, or PNG (max 5MB)</p>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Direct Deposit Authorization Agreement */}
      <div style={{ padding: '20px', backgroundColor: darkMode ? '#111827' : '#fffbeb', borderRadius: '8px', marginBottom: '24px', border: `1px solid ${darkMode ? '#374151' : '#fcd34d'}` }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: darkMode ? '#fcd34d' : '#92400e' }}>Direct Deposit Authorization Agreement</h4>
        
        <div style={{ color: darkMode ? '#d1d5db' : '#4b5563', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '16px' }}>
          <p style={{ marginBottom: '12px' }}>
            I hereby authorize {LYT_INFO.name} to initiate credit entries (deposits) to my account at the financial 
            institution named above. I also authorize the financial institution to accept and credit any such entries.
          </p>
          <p style={{ marginBottom: '12px' }}>
            This authorization will remain in effect until I notify {LYT_INFO.name} in writing to discontinue 
            these payments, allowing reasonable time to act on my request.
          </p>
          <p>
            I understand that if erroneous deposits are made to my account, {LYT_INFO.name} is authorized to 
            debit my account for an amount not to exceed the original amount of the erroneous credit.
          </p>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: '16px' }}>
          <input
            type="checkbox"
            name="directDepositAgreed"
            checked={formData.directDepositAgreed}
            onChange={handleChange}
            style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: accentPrimary }}
          />
          <span style={{ color: textColor, lineHeight: '1.5' }}>
            I have read and agree to the Direct Deposit Authorization terms above. I certify that the bank 
            account information provided is accurate.
          </span>
        </label>

        {formData.directDepositAgreed && (
          <>
            <SignaturePad
              onSignatureChange={(sig) => setFormData({ ...formData, directDepositSignature: sig })}
              label="Direct Deposit Authorization Signature"
              darkMode={darkMode}
            />
            <div style={{ marginTop: '12px' }}>
              <label style={labelStyle}>Date *</label>
              <input 
                type="date" 
                name="directDepositDate" 
                value={formData.directDepositDate} 
                onChange={handleChange} 
                required
                style={{ ...inputStyle, maxWidth: '200px' }} 
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderIdVerification = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>ID Verification</h3>
      <p style={{ color: colors.gray, marginBottom: '24px' }}>
        Please upload a valid government-issued photo ID for identity verification.
      </p>

      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>ID Type *</label>
        <select
          name="idType"
          value={formData.idType}
          onChange={handleChange}
          style={selectStyle}
        >
          <option value="drivers-license">Driver's License</option>
          <option value="state-id">State ID</option>
          <option value="passport">Passport</option>
          <option value="passport-card">Passport Card</option>
        </select>
      </div>

      <div>
        <label style={labelStyle}>Upload ID *</label>
        <div style={{ 
          border: `2px dashed ${darkMode ? '#374151' : '#ddd'}`, 
          borderRadius: '8px', 
          padding: '24px', 
          textAlign: 'center',
          backgroundColor: darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb'
        }}>
          {formData.idFileName ? (
            <div>
              <p style={{ color: accentSecondary, fontWeight: '500', marginBottom: '8px' }}>✓ {formData.idFileName}</p>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, idFile: null, idFileName: '' })}
                style={{ padding: '8px 16px', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                id="idFile"
                accept="image/*,.pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData({ ...formData, idFile: file, idFileName: file.name });
                  }
                }}
                style={{ display: 'none' }}
              />
              <label htmlFor="idFile" style={{ cursor: 'pointer' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Upload size={32} color={accentPrimary} />
                </div>
                <p style={{ color: accentPrimary, fontWeight: '500' }}>Click to upload your ID</p>
                <p style={{ fontSize: '0.8rem', color: colors.gray }}>PDF, JPG, or PNG (max 5MB)</p>
              </label>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px', marginTop: '24px' }}>
        <p style={{ fontSize: '0.9rem', color: colors.gray }}>
          <strong>Privacy Note:</strong> Your ID is securely stored and only used for employment verification purposes.
        </p>
      </div>
    </div>
  );

  const renderEmergencyContact = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>Emergency Contact</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Contact Name *</label>
          <input type="text" name="emergencyName" value={formData.emergencyName} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Relationship *</label>
          <input type="text" name="emergencyRelation" value={formData.emergencyRelation} onChange={handleChange} required style={inputStyle} placeholder="e.g., Spouse, Parent" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <div>
          <label style={labelStyle}>Phone Number *</label>
          <input type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" name="emergencyEmail" value={formData.emergencyEmail} onChange={handleChange} style={inputStyle} />
        </div>
      </div>
    </div>
  );

  const renderConsents = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>Employment Consents</h3>
      
      {/* Background Check Consent */}
      <div style={{ padding: '24px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px', marginBottom: '24px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
        <h4 style={{ fontWeight: '600', marginBottom: '16px', color: accentPrimary }}>Background Check Authorization</h4>
        <div style={{ color: colors.gray, lineHeight: '1.7', marginBottom: '16px', fontSize: '0.95rem' }}>
          <p style={{ marginBottom: '12px' }}>
            I hereby authorize {LYT_INFO.name} and its designated agents to conduct a comprehensive background investigation as part of my employment application. I understand this investigation may include, but is not limited to:
          </p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <li>Criminal history records check</li>
            <li>Employment verification</li>
            <li>Education verification</li>
            <li>Professional license verification</li>
            <li>Driving record check (if applicable to the position)</li>
            <li>Reference checks</li>
          </ul>
          <p style={{ marginBottom: '12px' }}>
            I understand that I am entitled to receive a copy of any consumer report obtained and a summary of my rights under the Fair Credit Reporting Act upon request.
          </p>
          <p>
            I release {LYT_INFO.name} and all persons, agencies, and entities providing information from any liability arising from requesting, obtaining, or using this information.
          </p>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="backgroundCheckConsent"
              checked={formData.backgroundCheckConsent}
              onChange={handleChange}
              style={{ width: '20px', height: '20px', marginTop: '2px' }}
            />
            <span style={{ lineHeight: '1.5' }}>
              I have read, understand, and consent to the background check investigation described above. I certify that all information I have provided is true and complete.
            </span>
          </label>
        </div>

        {formData.backgroundCheckConsent && (
          <div style={{ marginTop: '16px' }}>
            <label style={labelStyle}>Electronic Signature *</label>
            <SignaturePad
              signature={formData.backgroundCheckSignature}
              onSignatureChange={(sig) => setFormData({ ...formData, backgroundCheckSignature: sig })}
              darkMode={darkMode}
            />
          </div>
        )}
      </div>

      {/* Drug Test Consent */}
      <div style={{ padding: '24px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
        <h4 style={{ fontWeight: '600', marginBottom: '16px', color: accentPrimary }}>Drug & Alcohol Testing Consent</h4>
        <div style={{ color: colors.gray, lineHeight: '1.7', marginBottom: '16px', fontSize: '0.95rem' }}>
          <p style={{ marginBottom: '12px' }}>
            As a condition of employment with {LYT_INFO.name}, I understand and agree to the following drug and alcohol testing policy:
          </p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
            <li><strong>Pre-Employment Testing:</strong> I consent to a drug and/or alcohol test as part of the hiring process</li>
            <li><strong>Random Testing:</strong> I consent to unannounced random drug and/or alcohol testing during employment</li>
            <li><strong>Reasonable Suspicion:</strong> I consent to testing when there is reasonable suspicion of impairment</li>
            <li><strong>Post-Accident Testing:</strong> I consent to testing following workplace accidents or injuries</li>
            <li><strong>Return-to-Duty Testing:</strong> I consent to testing after any violation of the substance abuse policy</li>
          </ul>
          <p style={{ marginBottom: '12px' }}>
            I understand that a positive test result or refusal to submit to testing may result in withdrawal of a job offer, disciplinary action, or termination of employment.
          </p>
          <p>
            I authorize the release of test results to {LYT_INFO.name} and understand that results will be kept confidential in accordance with applicable laws.
          </p>
        </div>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="drugTestConsent"
              checked={formData.drugTestConsent}
              onChange={handleChange}
              style={{ width: '20px', height: '20px', marginTop: '2px' }}
            />
            <span style={{ lineHeight: '1.5' }}>
              I have read, understand, and consent to the drug and alcohol testing policy described above. I agree to comply with all testing requirements as a condition of my employment.
            </span>
          </label>
        </div>

        {formData.drugTestConsent && (
          <div style={{ marginTop: '16px' }}>
            <label style={labelStyle}>Electronic Signature *</label>
            <SignaturePad
              signature={formData.drugTestSignature}
              onSignatureChange={(sig) => setFormData({ ...formData, drugTestSignature: sig })}
              darkMode={darkMode}
            />
          </div>
        )}
      </div>

      <div style={{ padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px', marginTop: '24px' }}>
        <p style={{ fontSize: '0.9rem', color: colors.gray }}>
          <strong>Note:</strong> Both consents are required to proceed with your employment application. Your signatures confirm that you understand and agree to these policies.
        </p>
      </div>
    </div>
  );

  const renderSafetyTraining = () => (
    <div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>Safety Training Acknowledgment</h3>
      
      {/* HSE Manual Download */}
      <div style={{ 
        padding: '20px', 
        backgroundColor: darkMode ? '#0a2540' : '#dbeafe', 
        borderRadius: '8px', 
        marginBottom: '24px',
        border: `2px solid ${accentPrimary}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <FileText size={24} style={{ color: accentPrimary }} />
          <h4 style={{ fontWeight: '600', color: accentPrimary, margin: 0 }}>Required Reading: LYT HSE Safety Manual</h4>
        </div>
        <p style={{ color: darkMode ? '#94a3b8' : '#475569', marginBottom: '16px', lineHeight: '1.6' }}>
          Before signing below, you must download and read the complete LYT Communications Health, Safety & Environment (HSE) Manual. 
          This manual covers PPE requirements, trenching safety, HDD operations, aerial work, emergency procedures, and more.
        </p>
        <a 
          href="/LYT_HSE_Manual_v2.3.pdf" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: accentPrimary,
            color: 'white',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '0.95rem'
          }}
        >
          <FileText size={18} />
          Download HSE Manual v2.3 (PDF)
        </a>
      </div>

      <div style={{ padding: '24px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px', marginBottom: '24px' }}>
        <h4 style={{ fontWeight: '600', marginBottom: '16px' }}>Safety Commitment</h4>
        <p style={{ color: colors.gray, lineHeight: '1.7', marginBottom: '16px' }}>
          As {is1099 ? 'a contractor working with' : 'an employee of'} {LYT_INFO.name}, I understand and agree to the following:
        </p>
        <ul style={{ color: colors.gray, lineHeight: '1.8', paddingLeft: '20px' }}>
          <li>I have read and understand the LYT HSE Manual v2.3</li>
          <li>I will follow all safety procedures and protocols at all times</li>
          <li>I will wear required Personal Protective Equipment (PPE) on job sites</li>
          <li>I will report any unsafe conditions or incidents immediately</li>
          <li>I will attend all required safety training sessions</li>
          <li>I have Stop Work Authority and will use it when conditions are unsafe</li>
          <li>I will call 811 before any ground disturbance</li>
          <li>I understand that safety violations may result in disciplinary action</li>
        </ul>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            name="safetyAcknowledged"
            checked={formData.safetyAcknowledged}
            onChange={handleChange}
            style={{ width: '20px', height: '20px', marginTop: '2px' }}
          />
          <span style={{ lineHeight: '1.5' }}>
            I have read the LYT HSE Manual v2.3 and agree to comply with all {LYT_INFO.name} safety policies and procedures. 
            I understand that failure to follow these policies may result in disciplinary action up to and including termination.
          </span>
        </label>
      </div>

      <SignaturePad onSignatureChange={handleSafetySignatureChange} label="Signature" darkMode={darkMode} />
      
      <div style={{ marginTop: '16px' }}>
        <label style={labelStyle}>Date</label>
        <input type="date" name="safetyDate" value={formData.safetyDate} onChange={handleChange} style={{ ...inputStyle, maxWidth: '200px' }} />
      </div>
    </div>
  );

  // ── W-9 Tax Form (1099 mode only) ──
  const renderW9 = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>W-9 Request for Taxpayer Identification</h3>
        <button
          onClick={() => setShowW9Pdf(!showW9Pdf)}
          style={{
            padding: '8px 16px',
            backgroundColor: showW9Pdf ? accentPrimary : 'transparent',
            border: `1px solid ${accentPrimary}`,
            borderRadius: '6px',
            color: showW9Pdf ? '#fff' : accentPrimary,
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          {showW9Pdf ? 'Hide PDF' : 'View PDF Form'}
        </button>
      </div>

      {showW9Pdf && (
        <div style={{ marginBottom: '24px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${darkMode ? '#374151' : '#ddd'}` }}>
          <iframe
            src={`${URLS.w9Pdf}#toolbar=0&navpanes=0&scrollbar=1`}
            style={{
              width: '100%',
              height: isMobile ? '350px' : '600px',
              border: 'none',
              backgroundColor: '#fff',
            }}
            title="W-9 Form"
          />
          <div style={{ padding: '12px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderTop: `1px solid ${darkMode ? '#374151' : '#ddd'}`, textAlign: 'center' }}>
            <a
              href={URLS.w9Pdf}
              download="W-9_Form.pdf"
              style={{ color: accentPrimary, fontSize: '0.85rem', textDecoration: 'none' }}
            >
              Download PDF
            </a>
          </div>
        </div>
      )}

      {/* Tax Classification */}
      <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: '12px' }}>Federal Tax Classification *</label>
        <select
          name="taxClassification"
          value={formData.taxClassification}
          onChange={handleChange}
          required
          style={selectStyle}
        >
          <option value="individual">Individual/Sole Proprietor</option>
          <option value="llc-c">LLC - C Corporation</option>
          <option value="llc-s">LLC - S Corporation</option>
          <option value="llc-p">LLC - Partnership</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* SSN display (already collected in Personal Info) */}
      <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: '8px' }}>Social Security Number</label>
        <p style={{ color: textColor, fontSize: '0.95rem', margin: 0, fontFamily: 'monospace' }}>
          {formData.ssn ? `***-**-${formData.ssn.replace(/\D/g, '').slice(-4)}` : 'Not yet entered — go back to Personal Info'}
        </p>
        <p style={{ color: colors.gray, fontSize: '0.8rem', margin: '8px 0 0' }}>
          Full SSN will appear on the filed W-9 form.
        </p>
      </div>

      {/* Address display (already collected) */}
      <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: darkMode ? '#111827' : '#f8fafc', borderRadius: '8px' }}>
        <label style={{ ...labelStyle, marginBottom: '8px' }}>Address</label>
        <p style={{ color: textColor, fontSize: '0.9rem', margin: 0 }}>
          {formData.address ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}` : 'Not yet entered'}
        </p>
      </div>

      {/* IRS Certification */}
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px' }}>
        <p style={{ fontSize: '0.9rem', color: colors.gray, lineHeight: '1.6' }}>
          <strong>Certification:</strong> Under penalties of perjury, I certify that the number shown on this form is my correct taxpayer
          identification number, I am not subject to backup withholding, and I am a U.S. citizen or other U.S. person.
        </p>
      </div>

      {/* Signature */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ ...labelStyle, marginBottom: '12px' }}>Signature *</label>
        <SignaturePad
          onSignatureChange={(sig) => setFormData({ ...formData, w9Signature: sig })}
          existingSignature={formData.w9Signature}
          darkMode={darkMode}
        />
      </div>
      <div>
        <label style={labelStyle}>Date</label>
        <input type="date" name="w9Date" value={formData.w9Date} onChange={handleChange} style={inputStyle} />
      </div>
    </div>
  );

  // ── Independent Contractor Agreement (1099 mode only) ──
  const renderICA = () => {
    if (!onboardingContext) return null;
    const projectName = onboardingContext.project || 'the assigned project';
    const clientName = onboardingContext.client || 'the client';
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const sections = [
      {
        title: '1. INDEPENDENT CONTRACTOR STATUS',
        text: `Contractor is an independent contractor and not an employee, agent, partner, or joint venturer of ${LYT_INFO.name}. Contractor shall not be entitled to any employee benefits including, but not limited to, health insurance, retirement benefits, paid time off, workers\u2019 compensation, or unemployment insurance. Contractor acknowledges that ${LYT_INFO.name} will not withhold any taxes from payments and will issue a Form 1099-NEC for compensation paid.`,
      },
      {
        title: '2. SCOPE OF WORK',
        text: `Contractor agrees to perform fiber optic construction, installation, splicing, testing, and/or related services as directed by ${LYT_INFO.name} for the ${projectName} project (Client: ${clientName}). Specific tasks and schedules will be communicated by ${LYT_INFO.name} or its designated supervisors. Contractor shall perform all work in a professional and workmanlike manner consistent with industry standards.`,
      },
      {
        title: '3. COMPENSATION & PAYMENT',
        text: `Contractor shall be compensated at the rate(s) specified in the Rate Card (next step). Payment shall be made ${(onboardingContext.paymentTerms || 'Weekly').toLowerCase()} for hours worked and approved. Contractor must submit accurate time records. ${LYT_INFO.name} reserves the right to audit all time submissions. No overtime premium, holiday pay, or shift differential shall apply unless expressly agreed in writing.`,
      },
      {
        title: '4. TAXES & OBLIGATIONS',
        text: `Contractor is solely responsible for payment of all federal, state, and local income taxes, self-employment taxes (Social Security and Medicare), and any other taxes or contributions arising from compensation received. Contractor agrees to indemnify and hold ${LYT_INFO.name} harmless from any liability for such taxes, including penalties and interest.`,
      },
      {
        title: '5. INSURANCE & LIABILITY',
        text: `Contractor acknowledges that ${LYT_INFO.name} does not provide workers\u2019 compensation, general liability, or any other insurance coverage for Contractor. Contractor assumes all risk of injury, loss, or damage arising from Contractor\u2019s performance of services. Contractor agrees to maintain personal health insurance and any other coverage required by law. If Contractor operates a vehicle or heavy equipment on the job, Contractor shall maintain appropriate auto liability insurance.`,
      },
      {
        title: '6. INDEMNIFICATION',
        text: `Contractor shall indemnify, defend, and hold harmless ${LYT_INFO.name}, its officers, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys\u2019 fees) arising from or related to: (a) Contractor\u2019s performance of services; (b) Contractor\u2019s breach of this Agreement; (c) any injury or death to persons or damage to property caused by Contractor\u2019s negligence or willful misconduct; (d) any claim that Contractor is an employee of ${LYT_INFO.name}.`,
      },
      {
        title: '7. CONFIDENTIALITY',
        text: `Contractor shall maintain strict confidentiality regarding all proprietary information of ${LYT_INFO.name}, its clients, and project details including but not limited to: pricing, rate cards, client lists, project specifications, security protocols, and business operations. This obligation survives termination of this Agreement. Violation of confidentiality may result in immediate termination and legal action.`,
      },
      {
        title: '8. NON-SOLICITATION',
        text: `During the term of this Agreement and for twelve (12) months following termination, Contractor shall not directly or indirectly: (a) solicit or perform services for any client of ${LYT_INFO.name} that Contractor was introduced to through this engagement; (b) recruit, solicit, or hire any employee or contractor of ${LYT_INFO.name}; (c) interfere with any business relationship of ${LYT_INFO.name}.`,
      },
      {
        title: '9. SAFETY & COMPLIANCE',
        text: `Contractor shall comply with all applicable federal, state, and local laws, regulations, and ordinances, including OSHA standards, site-specific safety rules, and client security requirements. Contractor shall wear required Personal Protective Equipment (PPE) at all times on job sites. Contractor shall immediately report all accidents, injuries, near-misses, and property damage to ${LYT_INFO.name}. Failure to comply with safety requirements is grounds for immediate termination.`,
      },
      {
        title: '10. DRUG & ALCOHOL POLICY',
        text: `Contractor shall not possess, use, or be under the influence of alcohol, illegal drugs, or unauthorized controlled substances while performing services. ${LYT_INFO.name} reserves the right to require drug and alcohol testing at any time, including pre-engagement, random, post-accident, and for-cause testing. Refusal to submit to testing or a positive test result is grounds for immediate termination.`,
      },
      {
        title: '11. BACKGROUND CHECK CONSENT',
        text: `Contractor consents to background checks as required by ${LYT_INFO.name} or its clients, including criminal history, driving record, and identity verification. For airport and secured facility projects, Contractor consents to fingerprinting and security screening as required by facility operators and the TSA. Contractor certifies that all information provided is truthful and accurate.`,
      },
      {
        title: '12. TERMINATION',
        text: `Either party may terminate this Agreement at any time, with or without cause, by providing written or verbal notice. Upon termination, Contractor shall immediately return all company property, badges, credentials, and materials. ${LYT_INFO.name} shall pay Contractor for all approved hours worked through the date of termination. ${LYT_INFO.name} may terminate immediately for cause including but not limited to: safety violations, dishonesty, insubordination, no-call/no-show, or breach of this Agreement.`,
      },
      {
        title: '13. DISPUTE RESOLUTION',
        text: `This Agreement shall be governed by the laws of the State of Texas. Any disputes arising under this Agreement shall first be resolved through good-faith negotiation. If unresolved, disputes shall be submitted to binding arbitration in Harris County, Texas in accordance with the rules of the American Arbitration Association. The prevailing party shall be entitled to recover reasonable attorneys\u2019 fees and costs.`,
      },
      {
        title: '14. ENTIRE AGREEMENT',
        text: `This Agreement, together with the Rate Card, constitutes the entire agreement between the parties and supersedes all prior discussions, representations, or agreements. No modification of this Agreement shall be effective unless in writing and signed by both parties. If any provision is found unenforceable, the remaining provisions shall continue in full force and effect.`,
      },
    ];

    return (
      <div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px' }}>Independent Contractor Agreement</h3>
        <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '0.9rem', marginBottom: '24px' }}>
          Please read the entire agreement carefully before signing.
        </p>

        <div style={{
          padding: '24px',
          backgroundColor: darkMode ? '#111827' : '#f8fafc',
          borderRadius: '8px',
          marginBottom: '24px',
          maxHeight: '400px',
          overflowY: 'auto',
          border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
        }}>
          <p style={{ fontWeight: '600', marginBottom: '16px', fontSize: '0.95rem' }}>
            INDEPENDENT CONTRACTOR AGREEMENT
          </p>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', lineHeight: '1.7', marginBottom: '16px', fontSize: '0.9rem' }}>
            This Independent Contractor Agreement ("Agreement") is entered into as of {today} between{' '}
            <strong>{LYT_INFO.name}</strong> ("Company"), located at {LYT_INFO.address}, {LYT_INFO.city}, {LYT_INFO.state} {LYT_INFO.zip},{' '}
            and the undersigned individual ("Contractor").
          </p>

          {sections.map((section, idx) => (
            <div key={idx} style={{ marginBottom: '16px' }}>
              <p style={{ fontWeight: '600', fontSize: '0.85rem', marginBottom: '6px', color: darkMode ? '#e5e7eb' : '#1f2937' }}>
                {section.title}
              </p>
              <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', lineHeight: '1.7', fontSize: '0.85rem' }}>
                {section.text}
              </p>
            </div>
          ))}
        </div>

        <div style={{ padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px', marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.icaAccepted}
              onChange={(e) => setFormData({ ...formData, icaAccepted: e.target.checked })}
              style={{ width: '20px', height: '20px', marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ lineHeight: '1.5', fontSize: '0.95rem' }}>
              I have read and understand the Independent Contractor Agreement above. I agree to all terms and conditions.
              I acknowledge that I am entering into this agreement voluntarily as an independent contractor (1099) and not as an employee of {LYT_INFO.name}.
            </span>
          </label>
        </div>

        <SignaturePad
          onSignatureChange={(sig) => setFormData({ ...formData, icaSignature: sig })}
          label="Contractor Signature"
          darkMode={darkMode}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" name="icaDate" value={formData.icaDate} onChange={handleChange} style={{ ...inputStyle, maxWidth: '200px' }} />
          </div>
        </div>
      </div>
    );
  };

  // ── Rate Card (1099 mode only) ──
  const renderRateInput = () => {
    const inputStyle = getInputStyle(darkMode);
    const rateOptions = [
      { value: 'hourly', label: 'Hourly Rate', desc: 'Per hour worked' },
      { value: 'dayrate', label: 'Day Rate', desc: 'Flat rate per day worked' },
    ];
    return (
      <div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px' }}>
          Proposed Pay Rate
        </h3>
        <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
          Enter your desired pay rate below. This rate is subject to approval by LYT Communications.
          Payment is made <strong>{onboardingContext?.paymentTerms || 'Weekly'}</strong>.
        </p>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          {rateOptions.map(opt => (
            <div
              key={opt.value}
              onClick={() => setFormData({ ...formData, rateType: opt.value })}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '10px',
                border: `2px solid ${formData.rateType === opt.value ? accentPrimary : (darkMode ? '#374151' : '#e5e7eb')}`,
                backgroundColor: formData.rateType === opt.value ? `${accentPrimary}15` : (darkMode ? '#1f2937' : '#ffffff'),
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontWeight: '600', fontSize: '1rem', color: formData.rateType === opt.value ? accentPrimary : (darkMode ? '#f9fafb' : '#1f2937'), marginBottom: '4px' }}>
                {opt.label}
              </div>
              <div style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                {opt.desc}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: darkMode ? '#e5e7eb' : '#374151' }}>
            {formData.rateType === 'hourly' ? 'Hourly Rate' : 'Day Rate'}
          </label>
          <div style={{ position: 'relative', maxWidth: '240px' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '1.1rem', fontWeight: '600' }}>$</span>
            <input
              type="number"
              step="0.01"
              min="1"
              placeholder="0.00"
              value={formData.rateAmount}
              onChange={(e) => setFormData({ ...formData, rateAmount: e.target.value })}
              style={{ ...inputStyle, paddingLeft: '32px', fontSize: '1.2rem', fontWeight: '600' }}
            />
            <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '0.9rem' }}>
              / {formData.rateType === 'hourly' ? 'HR' : 'DAY'}
            </span>
          </div>
        </div>

        <div style={{ padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px', marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.rateCardAccepted}
              onChange={(e) => setFormData({ ...formData, rateCardAccepted: e.target.checked })}
              style={{ width: '20px', height: '20px', marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ lineHeight: '1.5' }}>
              I acknowledge the rate entered above is my proposed pay rate. I understand this rate is subject to approval by LYT Communications management.
              I understand I am being engaged as an independent contractor (1099) and am responsible for my own taxes.
            </span>
          </label>
        </div>

        <SignaturePad
          onSignatureChange={(sig) => setFormData({ ...formData, rateCardSignature: sig })}
          label="Signature"
          darkMode={darkMode}
        />
      </div>
    );
  };

  const renderRateCard = () => {
    if (!onboardingContext) return null;
    if (onboardingContext.allowRateInput) return renderRateInput();
    if (!onboardingContext.rates || onboardingContext.rates.length === 0) return null;
    const rates = onboardingContext.rates;
    return (
      <div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '24px' }}>
          {onboardingContext.project} — Pay Rate
        </h3>

        <div style={{ padding: '24px', backgroundColor: darkMode ? '#1f2937' : '#f8fafc', borderRadius: '8px', marginBottom: '24px' }}>
          <p style={{ color: darkMode ? '#d1d5db' : '#4b5563', lineHeight: '1.7', marginBottom: '16px' }}>
            Your pay rate for the {onboardingContext.project} project ({onboardingContext.client}).
            Payment is made <strong>{onboardingContext.paymentTerms || 'Weekly'}</strong>.
          </p>

          <div style={{ border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
              <thead>
                <tr style={{ backgroundColor: darkMode ? '#374151' : '#e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#1f2937', fontWeight: '600' }}>Position</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#1f2937', fontWeight: '600' }}>Unit</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', color: darkMode ? '#f9fafb' : '#1f2937', fontWeight: '600' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((item, idx) => (
                  <tr key={item.code} style={{ backgroundColor: idx % 2 === 0 ? (darkMode ? '#111827' : '#ffffff') : (darkMode ? '#1f2937' : '#f9fafb') }}>
                    <td style={{ padding: '12px 16px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, color: darkMode ? '#e5e7eb' : '#374151', fontWeight: '500' }}>{item.description}</td>
                    <td style={{ padding: '12px 16px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'center' }}>{item.uom}</td>
                    <td style={{ padding: '12px 16px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, color: accentPrimary, fontWeight: '600', textAlign: 'right', fontSize: '1.1rem' }}>{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ padding: '16px', backgroundColor: `${accentPrimary}10`, borderRadius: '8px', marginBottom: '24px' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.rateCardAccepted}
              onChange={(e) => setFormData({ ...formData, rateCardAccepted: e.target.checked })}
              style={{ width: '20px', height: '20px', marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ lineHeight: '1.5' }}>
              I acknowledge and accept the pay rate listed above for work performed on the {onboardingContext.project} project.
              I understand I am being engaged as an independent contractor (1099) and am responsible for my own taxes.
            </span>
          </label>
        </div>

        <SignaturePad
          onSignatureChange={(sig) => setFormData({ ...formData, rateCardSignature: sig })}
          label="Signature"
          darkMode={darkMode}
        />
      </div>
    );
  };

  const renderStepContent = () => {
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case 'personal': return renderPersonalInfo();
      case 'vehicle': return renderVehicleInfo();
      case 'w9': return renderW9();
      case 'ica': return renderICA();
      case 'w4': return renderW4Form();
      case 'rate-card': return renderRateCard();
      case 'direct-deposit': return renderDirectDeposit();
      case 'id-verification': return renderIdVerification();
      case 'emergency': return renderEmergencyContact();
      case 'consents': return renderConsents();
      case 'safety': return renderSafetyTraining();
      default: return null;
    }
  };

  const canProceed = () => {
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case 'personal':
        return formData.firstName && formData.lastName && formData.email && formData.phone && formData.ssn && formData.address && formData.city && formData.state && formData.zip;
      case 'vehicle':
        return formData.vehicleMake && formData.vehicleModel && formData.vehicleYear && formData.vehicleColor && formData.vehiclePlate && formData.vehiclePlateState;
      case 'w9':
        return formData.w9Signature;
      case 'ica':
        return formData.icaAccepted && formData.icaSignature;
      case 'w4':
        return formData.filingStatus && formData.w4Signature;
      case 'rate-card':
        if (onboardingContext?.allowRateInput) {
          return formData.rateType && formData.rateAmount &&
                 parseFloat(formData.rateAmount) > 0 &&
                 formData.rateCardAccepted && formData.rateCardSignature;
        }
        return formData.rateCardAccepted && formData.rateCardSignature;
      case 'direct-deposit':
        return formData.bankName && formData.routingNumber && formData.accountNumber && formData.directDepositSignature;
      case 'id-verification':
        return formData.idFile;
      case 'emergency':
        return formData.emergencyName && formData.emergencyRelation && formData.emergencyPhone;
      case 'consents':
        return formData.backgroundCheckConsent && formData.backgroundCheckSignature && formData.drugTestConsent && formData.drugTestSignature;
      case 'safety':
        return formData.safetyAcknowledged && formData.safetySignature;
      default:
        return false;
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <CheckCircle size={80} color={accentSecondary} style={{ marginBottom: '24px' }} />
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '16px', color: textColor }}>
            {is1099 ? 'Registration Complete!' : 'Welcome to the Team!'}
          </h2>
          <p style={{ color: colors.gray, fontSize: '1.1rem', marginBottom: '32px' }}>
            {is1099
              ? (onboardingContext?.requireApproval
                ? `Thank you for registering! Your proposed rate of $${parseFloat(formData.rateAmount || 0).toFixed(2)}/${formData.rateType === 'hourly' ? 'hr' : 'day'} has been submitted for approval. The office will contact you shortly to confirm.`
                : `Thank you for registering for the ${onboardingContext.project} project. Our team will contact you with next steps.`)
              : `Your onboarding paperwork has been submitted successfully. You'll receive a confirmation email shortly.`
            }
          </p>
          <button
            onClick={() => setCurrentPage('home')}
            style={{
              padding: '14px 28px',
              fontSize: '1rem',
              fontWeight: '600',
              backgroundColor: accentPrimary,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Return to Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, color: textColor }}>
      {/* Dark/Light Mode Toggle Bar */}
      <div style={{ backgroundColor: darkMode ? '#112240' : '#f1f5f9', padding: isMobile ? '8px 16px' : '6px 20px', display: 'flex', justifyContent: 'flex-end' }}>
        {setDarkMode && (
          <button onClick={() => setDarkMode(!darkMode)} aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"} style={{ backgroundColor: 'transparent', border: 'none', color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '8px 12px', borderRadius: '6px', minHeight: '44px' }}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Header - Triple tap title to skip steps (hidden feature) */}
      <header style={{ padding: isMobile ? '12px 16px' : '16px 20px', backgroundColor: darkMode ? '#0d1b2a' : '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
        <button
          onClick={() => setCurrentPage('onboarding')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'transparent', border: 'none', color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b', fontSize: isMobile ? '0.9rem' : '1rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', minHeight: '44px' }}
        >
          <ArrowLeft size={isMobile ? 18 : 20} /> {!isMobile && 'Back'}
        </button>
        <div
          onClick={handleHeaderTap}
          style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700', color: textColor, cursor: 'default', userSelect: 'none', textAlign: 'center' }}
        >
          <span style={{ color: accentPrimary }}>{is1099 ? 'Worker' : 'Employee'}</span> Onboarding
          {is1099 && onboardingContext.project && (
            <div style={{ fontSize: '0.75rem', fontWeight: '400', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '2px' }}>
              {onboardingContext.project}
            </div>
          )}
        </div>
        <button
          onClick={() => setCurrentPage('home')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', minHeight: '44px', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700' }}>
            <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          </div>
        </button>
      </header>

      {/* Progress Steps - Mobile: Dropdown, Desktop: Full bar */}
      {isMobile ? (
        <div style={{ padding: '12px 16px', backgroundColor: cardBg, borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
          <button
            onClick={() => setShowStepDropdown(!showStepDropdown)}
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: darkMode ? '#0d1b2a' : '#f1f5f9',
              border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              color: textColor,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: accentPrimary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '0.9rem',
              }}>
                {currentStep + 1}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{steps[currentStep].title}</div>
                <div style={{ fontSize: '0.75rem', color: colors.gray }}>Step {currentStep + 1} of {steps.length}</div>
              </div>
            </div>
            <ChevronDown size={20} style={{ transform: showStepDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          
          {showStepDropdown && (
            <div style={{
              marginTop: '8px',
              backgroundColor: darkMode ? '#0d1b2a' : '#fff',
              border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              {steps.map((step, idx) => (
                <div
                  key={step.id}
                  onClick={() => { setShowStepDropdown(false); }}
                  style={{
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: idx === currentStep ? (darkMode ? '#374151' : '#f1f5f9') : 'transparent',
                    borderBottom: idx < steps.length - 1 ? `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` : 'none',
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: idx < currentStep ? accentSecondary : idx === currentStep ? accentPrimary : (darkMode ? '#4b5563' : '#e5e7eb'),
                    color: idx <= currentStep ? '#fff' : colors.gray,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                  }}>
                    {idx < currentStep ? <CheckCircle size={16} /> : idx + 1}
                  </div>
                  <span style={{ color: idx <= currentStep ? textColor : colors.gray, fontSize: '0.9rem' }}>{step.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '20px', backgroundColor: cardBg, borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', justifyContent: 'space-between' }}>
            {steps.map((step, idx) => (
              <div
                key={step.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: idx <= currentStep ? accentPrimary : (darkMode ? '#0d1b2a' : '#e5e7eb'),
                    color: idx <= currentStep ? '#fff' : colors.gray,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    marginBottom: '8px',
                  }}
                >
                  {idx < currentStep ? <CheckCircle size={20} /> : idx + 1}
                </div>
                <span style={{ fontSize: '0.75rem', color: idx <= currentStep ? textColor : colors.gray, textAlign: 'center' }}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form Content */}
      <main style={{ padding: isMobile ? '20px 12px' : '40px 20px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ backgroundColor: cardBg, borderRadius: isMobile ? '12px' : '16px', padding: isMobile ? '16px' : '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            {renderStepContent()}

            {error && (
              <div role="alert" style={{ marginTop: '24px', padding: '12px', backgroundColor: `${accentError}20`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: accentError }}>
                <AlertCircle size={20} /> {error}
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: isMobile ? '24px' : '32px', paddingTop: isMobile ? '16px' : '24px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, gap: '12px' }}>
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                style={{
                  padding: isMobile ? '10px 16px' : '12px 24px',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${currentStep === 0 ? '#ccc' : colors.gray}`,
                  color: currentStep === 0 ? '#ccc' : textColor,
                  borderRadius: '8px',
                  cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flex: isMobile ? 1 : 'none',
                  justifyContent: 'center',
                }}
              >
                <ArrowLeft size={isMobile ? 16 : 18} /> {isMobile ? 'Back' : 'Previous'}
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  style={{
                    padding: isMobile ? '10px 16px' : '12px 24px',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    backgroundColor: canProceed() ? accentPrimary : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: canProceed() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: isMobile ? 1 : 'none',
                    justifyContent: 'center',
                  }}
                >
                  Next <ArrowRight size={isMobile ? 16 : 18} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  style={{
                    padding: isMobile ? '10px 16px' : '12px 24px',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    backgroundColor: canProceed() ? accentSecondary : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: canProceed() && !submitting ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: isMobile ? 1 : 'none',
                    justifyContent: 'center',
                  }}
                >
                  {submitting ? 'Submitting...' : (isMobile ? 'Complete' : 'Complete Onboarding')}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .hide-mobile-toggle { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default EmployeeOnboarding;
